import { isUnknownArray } from "../types/data";
import type { FilmMetadata } from "../types/media";
import { translateTextValue } from "../i18n/translations";
import { displayText } from "./displayValue";

const CYRILLIC_RE = /[\u0400-\u04FF]/;
const LATIN_RE = /\p{Script=Latin}/u;

const TITLE_TRANSLATIONS_EN: Record<string, string> = {
  "astrid i rafazella": "Astrid and Raphaelle",
  "navi. рожденные побеждать": "NAVI. Born to Win",
  "navi. рождённые побеждать": "NAVI. Born to Win",
  "navi. rozhdennye pobezhdat": "NAVI. Born to Win",
  "navi. rozhdennye pobezhdat'": "NAVI. Born to Win",
  "астрид и рафазэлла": "Astrid and Raphaelle",
  "аквамен": "Aquaman",
  "аннигиляция": "Annihilation",
  "воспоминания о будущем": "Memories of the Future",
  "веном": "Venom",
  "вход в никуда": "Entrance to Nowhere",
  "голодные игры: и вспыхнет пламя": "The Hunger Games: Catching Fire",
  "день, когда земля остановилась": "The Day the Earth Stood Still",
  "дети шпионов": "Spy Kids",
  "звезда родилась": "A Star Is Born",
  "и грянул гром": "A Sound of Thunder",
  "как приручить дракона": "How to Train Your Dragon",
  "кто убил blackberry": "Who Killed BlackBerry?",
  "кто убил blackberry?": "Who Killed BlackBerry?",
  "за гранью: я буду рядом. будущее": "Beyond the Edge: I Will Be Near. The Future",
  "легенда о пианисте": "The Legend of 1900",
  "моди: три дня на крыльях безумия": "Modi: Three Days on the Wing of Madness",
  "мстители: война бесконечности": "Avengers: Infinity War",
  "новый человек-паук: высокое напряжение": "The Amazing Spider-Man 2",
  "одна": "The One",
  "осенняя мелодия": "Autumn Melody",
  "очень страшное кино": "Scary Movie",
  "первому игроку приготовиться": "Ready Player One",
  "полицейская академия 2: их первое задание": "Police Academy 2: Their First Assignment",
  "посредник": "The Mediator",
  "подельники": "Accomplices",
  "русалочка": "The Little Mermaid",
  "рожденные побеждать": "Born to Win",
  "рождённые побеждать": "Born to Win",
  "сплит. звезда на грани": "Split: Star on the Edge",
  "стюарт литтл": "Stuart Little",
  "суперсемейка 2": "Incredibles 2",
  "сумрак": "Twilight",
  "хоттабыч": "Hottabych",
  "хрустальный цветок в увядающем мире": "Garakowa: Restore the World",
  "человек-паук: через вселенные": "Spider-Man: Into the Spider-Verse",
  "13-и район": "District B13",
  "13-й район": "District B13",
  "экспедиция выбрана": "Expedition selected"
};

const CYRILLIC_TO_LATIN: Record<string, string> = {
  А: "A", а: "a", Б: "B", б: "b", В: "V", в: "v", Г: "G", г: "g",
  Д: "D", д: "d", Е: "E", е: "e", Ё: "Yo", ё: "yo", Ж: "Zh", ж: "zh",
  З: "Z", з: "z", И: "I", и: "i", Й: "Y", й: "y", К: "K", к: "k",
  Л: "L", л: "l", М: "M", м: "m", Н: "N", н: "n", О: "O", о: "o",
  П: "P", п: "p", Р: "R", р: "r", С: "S", с: "s", Т: "T", т: "t",
  У: "U", у: "u", Ф: "F", ф: "f", Х: "Kh", х: "kh", Ц: "Ts", ц: "ts",
  Ч: "Ch", ч: "ch", Ш: "Sh", ш: "sh", Щ: "Shch", щ: "shch",
  Ъ: "", ъ: "", Ы: "Y", ы: "y", Ь: "", ь: "", Э: "E", э: "e",
  Ю: "Yu", ю: "yu", Я: "Ya", я: "ya",
  І: "I", і: "i", Ї: "Yi", ї: "yi", Є: "Ye", є: "ye", Ґ: "G", ґ: "g"
};

export const hasCyrillicText = (value: string) => CYRILLIC_RE.test(String(value || ""));

const isReadableEnglishTitle = (value: string) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const letters = text.match(/\p{L}/gu) || [];
  return Boolean(
    text &&
    letters.length > 0 &&
    letters.every((letter) => LATIN_RE.test(letter))
  );
};

const normalizeTitle = (value: unknown) =>
  displayText(value, "").replace(/[\u200B-\u200D\uFEFF]/g, "").replace(/\s+/g, " ").trim();

const romanizeCyrillicTitle = (value: string) => {
  const romanized = normalizeTitle(value).replace(/[\u0400-\u04FF]/g, (char) => CYRILLIC_TO_LATIN[char] ?? char);
  return normalizeTitle(romanized);
};

const normalizeTitleKey = (value: unknown) =>
  normalizeTitle(value)
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[“”«»"']/g, "")
    .replace(/[^\p{L}\p{N}:+.\s-]+/gu, "")
    .replace(/\s+/g, " ")
    .trim();

export const getKnownEnglishMovieTitle = (value: string) =>
  TITLE_TRANSLATIONS_EN[normalizeTitleKey(value)] || "";

const getEnglishTranslationValues = (film: FilmMetadata | null | undefined) => {
  const translations = [
    film?.translations,
    film?.filmData?.translations,
    film?.fullDataFilm?.translations,
    film?.details?.translations
  ];

  return translations.flatMap((source) => {
    if (!source) return [];
    if (isUnknownArray(source?.translations)) {
      return source.translations
        .filter((item) => String(item?.iso_639_1 || item?.language || "").toLowerCase() === "en")
        .flatMap((item) => [item?.data?.title, item?.data?.name, item?.title, item?.name]);
    }
    return [
      typeof source.en === "object" ? source.en.title : undefined,
      typeof source.en === "object" ? source.en.name : undefined,
      source?.en,
      typeof source.english === "object" ? source.english.title : undefined,
      typeof source.english === "object" ? source.english.name : undefined,
      source?.english
    ];
  });
};

const localizedEnglishTitle = (value: FilmMetadata["localizedTitle"]) =>
  typeof value === "object" && value !== null ? value.en : undefined;

const getCandidateValues = (film: FilmMetadata | null | undefined) => [
  film?.titleEn,
  film?.title_en,
  film?.englishTitle,
  film?.english_title,
  film?.enTitle,
  film?.en_title,
  localizedEnglishTitle(film?.localizedTitle),
  film?.localizedTitles?.en,
  film?.displayTitleEn,
  film?.display_title_en,
  film?.displayTitle,
  film?.movieTitle,
  film?.filmTitle,
  film?.filmData?.titleEn,
  film?.filmData?.title_en,
  film?.filmData?.englishTitle,
  film?.filmData?.english_title,
  localizedEnglishTitle(film?.filmData?.localizedTitle),
  film?.fullDataFilm?.titleEn,
  film?.fullDataFilm?.title_en,
  film?.fullDataFilm?.englishTitle,
  film?.fullDataFilm?.english_title,
  localizedEnglishTitle(film?.fullDataFilm?.localizedTitle),
  film?.details?.titleEn,
  film?.details?.title_en,
  film?.details?.englishTitle,
  film?.details?.english_title,
  localizedEnglishTitle(film?.details?.localizedTitle),
  ...getEnglishTranslationValues(film),
  film?.filmData?.title,
  film?.filmData?.name,
  film?.fullDataFilm?.title,
  film?.fullDataFilm?.name,
  film?.details?.title,
  film?.details?.name,
  film?.original_title,
  film?.original_name,
  film?.originalTitle,
  film?.originalName,
  film?.filmOriginalTitle,
  film?.importedOriginalTitle
].map(normalizeTitle).filter(Boolean);

export const getLocalizedMovieTitle = (film: FilmMetadata | null | undefined, language = "ru", fallback = "Неизвестный фильм") => {
  const primary = [
    film?.title,
    film?.filmTitle,
    film?.name,
    film?.displayTitle,
    film?.originalTitle,
    fallback
  ].map(normalizeTitle).find(Boolean) || normalizeTitle(fallback);

  if (language !== "en") return primary;

  const translatedPrimary = normalizeTitle(translateTextValue(primary, "en"));
  const knownPrimary = getKnownEnglishMovieTitle(primary);
  if (knownPrimary) return knownPrimary;
  if (translatedPrimary && translatedPrimary !== primary) return translatedPrimary;
  if (isReadableEnglishTitle(primary)) return primary;

  const readableCandidate = getCandidateValues(film)
    .map((candidate) => {
      const knownTitle = getKnownEnglishMovieTitle(candidate);
      if (knownTitle) return knownTitle;
      const translated = normalizeTitle(translateTextValue(candidate, "en"));
      return translated && translated !== candidate ? translated : candidate;
    })
    .find(isReadableEnglishTitle);

  const fallbackTitle = readableCandidate || translatedPrimary;
  if (isReadableEnglishTitle(fallbackTitle)) return fallbackTitle;

  const romanizedPrimary = romanizeCyrillicTitle(primary);
  if (romanizedPrimary && !hasCyrillicText(romanizedPrimary)) return romanizedPrimary;

  return isReadableEnglishTitle(fallbackTitle) ? fallbackTitle : primary;
};
